import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";

import { AssessmentsDocument, Question } from "./assessmentsTypes";
import styles from "./CompanyAssessments.module.css";
import { updateQuestion } from "./assessmentsMutations";
import { MAX_PROMPT_LENGTH } from "./assessmentsSchema";

interface QuestionEditorDialogProps {
    open: boolean;
    assessmentId: string;
    question: Question | null;
    document: AssessmentsDocument;
    onOpenChange: (open: boolean) => void;
    onDraftChange: (document: AssessmentsDocument) => void;
}

export function QuestionEditorDialog({
    open,
    assessmentId,
    question,
    document,
    onOpenChange,
    onDraftChange
}: QuestionEditorDialogProps) {
    const { t } = useTranslation();
    const [promptDraft, setPromptDraft] = useState("");
    const [minDraft, setMinDraft] = useState(1);
    const [maxDraft, setMaxDraft] = useState(10);
    const [minLabelDraft, setMinLabelDraft] = useState("");
    const [maxLabelDraft, setMaxLabelDraft] = useState("");
    const [choiceSetIdDraft, setChoiceSetIdDraft] = useState("");

    useEffect(() => {
        if (!open || !question) {
            return;
        }
        setPromptDraft(question.prompt);
        if (question.type === "scale") {
            setMinDraft(question.min);
            setMaxDraft(question.max);
            setMinLabelDraft(question.min_label ?? "");
            setMaxLabelDraft(question.max_label ?? "");
        }
        if (question.type === "multiple_choice") {
            setChoiceSetIdDraft(question.choice_set_id);
        }
    }, [open, question?.id, question]);

    if (!question) {
        return null;
    }

    const handleSave = () => {
        if (question.type === "scale") {
            onDraftChange(
                updateQuestion(document, assessmentId, question.id, {
                    prompt: promptDraft,
                    min: minDraft,
                    max: maxDraft,
                    min_label: minLabelDraft,
                    max_label: maxLabelDraft
                })
            );
        } else if (question.type === "multiple_choice") {
            onDraftChange(
                updateQuestion(document, assessmentId, question.id, {
                    prompt: promptDraft,
                    choice_set_id: choiceSetIdDraft
                })
            );
        } else {
            onDraftChange(
                updateQuestion(document, assessmentId, question.id, {
                    prompt: promptDraft
                })
            );
        }
        onOpenChange(false);
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />
                <Dialog.Content className={styles.dialogContent}>
                    <Dialog.Title className={styles.dialogTitle}>
                        {t(`settingsPage.company.assessments.questionTypes.${question.type}`)}
                    </Dialog.Title>

                    <label className={styles.dialogFieldLabel}>
                        {t("settingsPage.company.assessments.questionPrompt")}
                        <textarea
                            className={styles.dialogTextarea}
                            value={promptDraft}
                            maxLength={MAX_PROMPT_LENGTH}
                            onChange={event => setPromptDraft(event.target.value)}
                        />
                    </label>

                    {question.type === "scale" ? (
                        <>
                            <label className={styles.dialogFieldLabel}>
                                {t("settingsPage.company.assessments.scaleMin")}
                                <input
                                    className={styles.dialogInput}
                                    type="number"
                                    value={minDraft}
                                    onChange={event => setMinDraft(Number(event.target.value))}
                                />
                            </label>
                            <label className={styles.dialogFieldLabel}>
                                {t("settingsPage.company.assessments.scaleMax")}
                                <input
                                    className={styles.dialogInput}
                                    type="number"
                                    value={maxDraft}
                                    onChange={event => setMaxDraft(Number(event.target.value))}
                                />
                            </label>
                            <label className={styles.dialogFieldLabel}>
                                {t("settingsPage.company.assessments.scaleMinLabel")}
                                <input
                                    className={styles.dialogInput}
                                    type="text"
                                    value={minLabelDraft}
                                    onChange={event => setMinLabelDraft(event.target.value)}
                                />
                            </label>
                            <label className={styles.dialogFieldLabel}>
                                {t("settingsPage.company.assessments.scaleMaxLabel")}
                                <input
                                    className={styles.dialogInput}
                                    type="text"
                                    value={maxLabelDraft}
                                    onChange={event => setMaxLabelDraft(event.target.value)}
                                />
                            </label>
                        </>
                    ) : null}

                    {question.type === "multiple_choice" ? (
                        <label className={styles.dialogFieldLabel}>
                            {t("settingsPage.company.assessments.choiceSet")}
                            <select
                                className={styles.dialogSelect}
                                value={choiceSetIdDraft}
                                onChange={event => setChoiceSetIdDraft(event.target.value)}
                            >
                                {document.choice_sets.map(choiceSet => (
                                    <option key={choiceSet.id} value={choiceSet.id}>
                                        {choiceSet.title?.trim() ||
                                            t("settingsPage.company.assessments.untitledChoiceSet")}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : null}

                    <div className={styles.dialogActions}>
                        <div />
                        <div className={styles.dialogPrimaryActions}>
                            <Dialog.Close asChild>
                                <button type="button" className={styles.dialogCancelButton}>
                                    {t("settingsPage.account.cancel")}
                                </button>
                            </Dialog.Close>
                            <button type="button" className={styles.dialogSaveButton} onClick={handleSave}>
                                {t("settingsPage.account.save")}
                            </button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
